// @core/stores/webhook/WebhookStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { webhookApi } from '@core/api';
import type {
  CreateEndpointRequest,
  DeliveryResponse,
  DisableEndpointRequest,
  EnableEndpointRequest,
  EndpointResponse,
  EndpointStatsResponse,
  ListEndpointsResponse,
  ListEventTypesResponse,
  RetryDeliveryRequest,
  RotateSecretRequest,
  RotateSecretResponse,
  TestEndpointRequest,
  TestEndpointResponse,
  UpdateEndpointRequest,
  WebhookDelivery,
  WebhookStatsResponse,
} from '@core/types/webhook';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class WebhookStore {
  // List state
  items: WebhookDelivery[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: WebhookDelivery | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, WebhookDelivery>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): WebhookDelivery | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: WebhookDelivery | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create webhook endpoint
   */
  async CreateEndpoint(data: CreateEndpointRequest): Promise<EndpointResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.CreateEndpoint(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete webhook endpoint
   */
  async DeleteEndpoint(endpoint_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await webhookApi.DeleteEndpoint(endpoint_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== endpoint_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === endpoint_id) {
          this.currentItem = null;
        }

        this.removeFromCache(endpoint_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Disable webhook endpoint
   */
  async DisableEndpoint(endpoint_id: string, data: DisableEndpointRequest): Promise<EndpointResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.DisableEndpoint(endpoint_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Enable webhook endpoint
   */
  async EnableEndpoint(endpoint_id: string, data: EnableEndpointRequest): Promise<EndpointResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.EnableEndpoint(endpoint_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get delivery details
   */
  async GetDelivery(endpoint_id: string, delivery_id: string): Promise<WebhookDelivery> {
    // Check cache first
    const cached = this.cache.get(delivery_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await webhookApi.GetDelivery(endpoint_id, delivery_id);
      const item = response.delivery;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get webhook endpoint
   */
  async GetEndpoint(endpoint_id: string): Promise<EndpointResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.GetEndpoint(endpoint_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get endpoint statistics
   */
  async GetEndpointStats(endpoint_id: string): Promise<EndpointStatsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.GetEndpointStats(endpoint_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get webhook statistics
   */
  async GetWebhookStats(params?: { }): Promise<WebhookStatsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.GetWebhookStats(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List webhook deliveries
   */
  async ListDeliveries(endpoint_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await webhookApi.ListDeliveries(endpoint_id, params);

      runInAction(() => {
        this.items = response.deliveries?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List webhook endpoints
   */
  async ListEndpoints(params?: PaginatedRequest): Promise<ListEndpointsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.ListEndpoints(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List available event types
   */
  async ListEventTypes(params?: PaginatedRequest): Promise<ListEventTypesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.ListEventTypes(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Retry failed delivery
   */
  async RetryDelivery(endpoint_id: string, delivery_id: string, data: RetryDeliveryRequest): Promise<DeliveryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.RetryDelivery(endpoint_id, delivery_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Rotate webhook secret
   */
  async RotateSecret(endpoint_id: string, data: RotateSecretRequest): Promise<RotateSecretResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.RotateSecret(endpoint_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Send test webhook
   */
  async TestEndpoint(endpoint_id: string, data: TestEndpointRequest): Promise<TestEndpointResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.TestEndpoint(endpoint_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update webhook endpoint
   */
  async UpdateEndpoint(endpoint_id: string, data: UpdateEndpointRequest): Promise<EndpointResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await webhookApi.UpdateEndpoint(endpoint_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const webhookStore = new WebhookStore();
